import type { DeliveryChannel, DeliveryMetric } from '@/features/metrics-charts';
import { generatePath } from '@/services/router/generate-path';

/**
 * The classic dashboard's URLs. The migrated page drops the `journeys`
 * segment; the `journeys` spelling stays claimed so the embedded Ember app
 * can keep answering it while the rollout flag is off, and so links written
 * either way keep working.
 */

/**
 * The parameter that asks the `journeys` URL for the classic dashboard rather
 * than for the workspace landing page. The migrated URL needs no such ask —
 * it means only the one page — so every handoff between the two adds or drops
 * it to keep the reader where they were going.
 */
const CLASSIC_PARAM = 'legacy';

export function dashboardPath(workspaceId: string): string {
  return generatePath('/workspaces/:workspaceId/dashboard', { workspaceId });
}

export function legacyDashboardPath(workspaceId: string): string {
  return generatePath('/workspaces/:workspaceId/journeys/dashboard', { workspaceId });
}

/** The workspace landing page, which the `journeys` URL means unless it asks for the classic one. */
export function homePath(workspaceId: string): string {
  return generatePath('/workspaces/:workspaceId/home', { workspaceId });
}

/**
 * The Sent list narrowed to one channel and one measure, which is where a chart
 * card's "View log" leads. The list reads both filters from these parameters,
 * and Ember's dashboard writes the same URL (`messageActivitySentUrl`).
 */
export function messageLogPath(workspaceId: number, channel: DeliveryChannel, metric: DeliveryMetric): string {
  return generatePath(
    '/workspaces/:workspaceId/message_activity/sent',
    { workspaceId },
    { actionType: channel, deliveryStatus: metric },
  );
}

/** Whether a query asks for the classic dashboard by name. */
export function asksForClassicDashboard(searchStr: string): boolean {
  return new URLSearchParams(searchStr).has(CLASSIC_PARAM);
}

/** The same query, asking for the classic dashboard by name. */
export function askForClassicDashboard(searchStr: string): string {
  const params = new URLSearchParams(searchStr);
  params.set(CLASSIC_PARAM, '');
  return `?${params.toString()}`;
}

/** A path with the query and fragment a location carries, in URL order. */
export function withLocation(path: string, searchStr: string, hash: string): string {
  const fragment = hash === '' ? '' : `#${hash.replace(/^#/, '')}`;
  return `${path}${searchStr}${fragment}`;
}
